import { LoadingOutlined } from '@ant-design/icons';
import { Spin, Tooltip } from 'antd';
import { Checkbox } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { CrossSvg, SearchSvg } from '../../../assets/icons';
import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import theme from '../../../assets/theme';
import { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { useGenerateStandardFromSearch } from '../../../hooks/useGenerateStandardFromSearch';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useStandards from '../../../hooks/useStandards';
import {
  AugmentedStandards,
  PromptResourceMap,
  Standards,
} from '../../../services/backendService/types';
import {
  augmentRawStandard,
  sortAugmentedStandardsInplace,
} from '../../../utils/standardsStrandParsingUtils';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { FloatingSelect } from './components/FloatingSelect';

export const ResponsiveGeneratorsGridContainer = styled.div<{ $isCentered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;

  @media (max-width: 4096px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1440px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 800px) {
    // 800 we go into mobile screen mode
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }

  ${({ $isCentered }) =>
    $isCentered &&
    css`
      max-width: 500px;
      margin: 0 auto;
      @media (max-width: 4096px) {
        grid-template-columns: 1fr 1fr 1fr;
      }
      @media (max-width: 700px) {
        grid-template-columns: 1fr 1fr;
      }
    `}
`;

export type LearningStandard = {
  label: string;
  code: string;
  subjects: Subject[];
  icon: JSX.Element;
  color: string;
};

export type Subject = {
  label: string;
  icon: JSX.Element;
  color: string;
  id: string;
  placeholder: string;
};

export type GeneratedStandard = {
  id: string;
  'standard-code'?: string;
  'standard-description'?: string;
  'standard-relevancy'?: string;
};

const toStandardsIntercomMessage = ({
  stateDropdownOption,
  subject,
  grade,
}: {
  stateDropdownOption?: string;
  subject: string;
  grade?: string;
}) => `Hey! I cannot find the standards I'm looking for. Can you guys please add them?
                        
State/Standard: ${stateDropdownOption}.
Subject: ${subject}.
Grade: ${grade || ''}.

(Please share what standards you're looking for)
                        
I'm looking for: `;

const stateOptions = [
  { displayName: 'Alabama', value: 'AL' },
  { displayName: 'Alaska', value: 'AK' },
  { displayName: 'Arizona', value: 'AZ' },
  { displayName: 'Arkansas', value: 'AR' },
  // { displayName: 'Armed Forces in Europe', value: 'AE' },
  // { displayName: 'American Samoa', value: 'AS' },
  { displayName: 'California', value: 'CA' },
  { displayName: 'Colorado', value: 'CO' },
  { displayName: 'Connecticut', value: 'CT' },
  { displayName: 'Delaware', value: 'DE' },
  { displayName: 'Florida', value: 'FL' },
  { displayName: 'Georgia', value: 'GA' },
  { displayName: 'Hawaii', value: 'HI' },
  { displayName: 'Idaho', value: 'ID' },
  { displayName: 'Illinois', value: 'IL' },
  { displayName: 'Indiana', value: 'IN' },
  { displayName: 'Iowa', value: 'IA' },
  { displayName: 'Kansas', value: 'KS' },
  { displayName: 'Kentucky', value: 'KY' },
  { displayName: 'Louisiana', value: 'LA' },
  { displayName: 'Maine', value: 'ME' },
  { displayName: 'Maryland', value: 'MD' },
  { displayName: 'Massachusetts', value: 'MA' },
  { displayName: 'Michigan', value: 'MI' },
  { displayName: 'Minnesota', value: 'MN' },
  { displayName: 'Mississippi', value: 'MS' },
  { displayName: 'Missouri', value: 'MO' },
  { displayName: 'Montana', value: 'MT' },
  { displayName: 'Nebraska', value: 'NE' },
  { displayName: 'Nevada', value: 'NV' },
  { displayName: 'New Hampshire', value: 'NH' },
  { displayName: 'New Jersey', value: 'NJ' },
  { displayName: 'New Mexico', value: 'NM' },
  { displayName: 'New York', value: 'NY' },
  { displayName: 'North Carolina', value: 'NC' },
  { displayName: 'North Dakota', value: 'ND' },
  { displayName: 'Ohio', value: 'OH' },
  { displayName: 'Oklahoma', value: 'OK' },
  { displayName: 'Oregon', value: 'OR' },
  { displayName: 'Pennsylvania', value: 'PA' },
  { displayName: 'Rhode Island', value: 'RI' },
  { displayName: 'South Carolina', value: 'SC' },
  { displayName: 'South Dakota', value: 'SD' },
  { displayName: 'Tennessee', value: 'TN' },
  { displayName: 'Texas', value: 'TX' },
  { displayName: 'Utah', value: 'UT' },
  { displayName: 'Vermont', value: 'VT' },
  { displayName: 'Virginia', value: 'VA' },
  { displayName: 'Washington', value: 'WA' },
  { displayName: 'West Virginia', value: 'WV' },
  { displayName: 'Wisconsin', value: 'WI' },
  { displayName: 'Wyoming', value: 'WY' },
];

const nationalStandardOptionsBySubject = {
  ela: [{ displayName: 'Common Core ELA', value: 'CCSS' }],
  math: [{ displayName: 'Common Core Math', value: 'CCSS-M' }],
  science: [{ displayName: 'Next Generation Science Standards (NGSS)', value: 'NGSS' }],
  socialStudies: [
    { displayName: 'National Council for Social Studies (NCSS)', value: 'NCSS' },
  ],
};

// Maps human-readable subject names to internal IDs
const normalizeSubjectId = (
  subject: string,
): keyof typeof nationalStandardOptionsBySubject => {
  const lower = subject.toLowerCase();
  if (
    lower.includes('english') ||
    lower.includes('ela') ||
    lower.includes('language arts')
  ) {
    return 'ela';
  }
  if (lower.includes('math')) {
    return 'math';
  }
  if (lower.includes('science')) {
    return 'science';
  }
  if (lower.includes('social') || lower.includes('history') || lower.includes('civics')) {
    return 'socialStudies';
  }
  return 'ela'; // fallback to ELA
};

const gradeOptions = [
  { displayName: 'K', value: 'Kindergarten' },
  { displayName: '1st', value: '1st' },
  { displayName: '2nd', value: '2nd' },
  { displayName: '3rd', value: '3rd' },
  { displayName: '4th', value: '4th' },
  { displayName: '5th', value: '5th' },
  { displayName: '6th', value: '6th' },
  { displayName: '7th', value: '7th' },
  { displayName: '8th', value: '8th' },
  { displayName: '9th', value: '9th' },
  { displayName: '10th', value: '10th' },
  { displayName: '11th', value: '11th' },
  { displayName: '12th', value: '12th' },
];

const searchPlaceholderMapper = (subject: string) => {
  if (subject === 'ela') {
    return '"Citing evidence", "Character analysis"';
  }
  if (subject === 'socialStudies') {
    return '"The American Revolution", "Early Texas"';
  }
  if (subject === 'science') {
    return '"Water cycle", "Photosynthesis"';
  }
  if (subject === 'math') {
    return '"Adding fractions", "Place value"';
  }
  return '';
};

const getStateDropDownOptions = (subject: string) => {
  const nationalStandards =
    nationalStandardOptionsBySubject[
      subject as keyof typeof nationalStandardOptionsBySubject
    ];
  return [...nationalStandards, ...stateOptions];
};

export const StandardsPicker = ({
  onStandardsSelected,
  presetSubject,
  presetState,
  presetGrade,
  canSwitchSubject,
  initialSelectedStandards = [],
}: {
  onStandardsSelected: (standards: Standards[]) => void;
  presetSubject?: { id: string; label: string };
  presetState?: string;
  presetGrade?: string;
  canSwitchSubject?: boolean;
  initialSelectedStandards?: Standards[];
}) => {
  const { userAccessBadge, paywallComponent, promptPaywall } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const hasAccessToSchoolPlan =
    userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;

  // Pre-populate from access badge if not provided
  const defaultState = presetState || userAccessBadge?.organization?.region;
  const defaultGrade = presetGrade || userAccessBadge?.user.profileInfo?.grades?.[0];
  const defaultSubject =
    presetSubject ||
    (() => {
      const profileSubject = userAccessBadge?.user.profileInfo?.subjects?.[0];
      if (profileSubject) {
        return { id: normalizeSubjectId(profileSubject), label: profileSubject };
      }
      return { id: 'ela', label: 'ela' };
    })();

  const [selectedState, setSelectedState] = useState<string | undefined>(defaultState);
  const [selectedNationalStandard, setSelectedNationalStandard] = useState<
    string | undefined
  >(undefined);

  const metrics = useMetrics();

  const isDesktop = useBreakPoint(700);

  const { showNewMessages } = useIntercom();

  const [subjectSelected, setSubjectSelected] = useState<{ id: string; label: string }>(
    defaultSubject,
  );
  const [gradeSelected, setGradeSelected] = useState<string>(defaultGrade || '4th');
  // Multi-select state for standards
  const [selectedStandards, setSelectedStandards] = useState<Standards[]>(
    initialSelectedStandards,
  );
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [dropdownOptions] = useState(getStateDropDownOptions(subjectSelected.id));

  const handleSetStateDropdownOption = (value: string) => {
    // Check whether user selected a national standard
    const isNationalStandard = nationalStandardOptionsBySubject[
      subjectSelected.id as keyof typeof nationalStandardOptionsBySubject
    ].find(o => o.value === value);
    if (isNationalStandard) {
      setSelectedNationalStandard(value);
      setSelectedState(undefined);
    } else {
      setSelectedState(value);
      setSelectedNationalStandard(undefined);
    }
  };

  const handleSetGrade = (grade: string) => {
    setGradeSelected(grade);
    // setSearchValue(undefined);
  };

  const { standards: rawStrands, isFetchingStandards } = useStandards({
    isGenerator: true,
  });
  const strands: AugmentedStandards[] = useMemo(() => {
    const augmentedStandards: AugmentedStandards[] = rawStrands
      ?.map((rawStrand: Standards) => {
        return augmentRawStandard(rawStrand);
      })
      ?.filter(Boolean) as AugmentedStandards[];
    sortAugmentedStandardsInplace(augmentedStandards);
    return augmentedStandards;
  }, [rawStrands]);

  const mustGenerateStandards = useMemo(() => {
    // If user has selected any national standards other than CCSS
    if (selectedNationalStandard && selectedNationalStandard !== 'CCSS') return true;

    // Check if user selected Common Core ELA standards
    const statesThatUseCommonCoreForELA = [
      'AL',
      'CA',
      'CT',
      'DE',
      'HI',
      'IL',
      'KY',
      'MD',
      'MA',
      'NJ',
      'NY',
      'NC',
      'OR',
      'RI',
      'TX',
      'VT',
      'WV',
      'WA',
    ];
    const userRequestedCommonCoreELAStandards =
      subjectSelected.id === 'ela' &&
      (selectedNationalStandard === 'CCSS' ||
        statesThatUseCommonCoreForELA.includes(selectedState ?? ''));
    if (userRequestedCommonCoreELAStandards) {
      return false;
    }

    // Check if user has elected a state (and this state is not Texas)
    return selectedState !== 'TX';
  }, [selectedState, selectedNationalStandard, subjectSelected.id]);

  const {
    generate,
    data: generatedStandards,
    isLoading: isGeneratingStandards,
    reset: resetGeneratedStandards,
  } = useGenerateStandardFromSearch();

  const handleGenerateStandards = useCallback(
    (value: string) => {
      if (mustGenerateStandards && !isGeneratingStandards) {
        generate({
          prompt: {
            searchValue: value,
            standardSelected: selectedState
              ? stateOptions.find(o => o.value === selectedState)?.displayName
              : nationalStandardOptionsBySubject[
                  subjectSelected.id as keyof typeof nationalStandardOptionsBySubject
                ].find(o => o.value === selectedNationalStandard)?.displayName,
            subjectSelected: subjectSelected?.label,
            gradeSelected,
          },
        });
      }
    },
    [
      generate,
      gradeSelected,
      selectedState,
      selectedNationalStandard,
      subjectSelected.id,
      subjectSelected?.label,
      mustGenerateStandards,
      isGeneratingStandards,
    ],
  );

  const MAX_STANDARDS = 3;

  const handleConfirm = () => {
    if (selectedStandards.length > 0) {
      metrics.logEvent('CreatePage.StandardsSelectedStandardPicker', {
        standardsCount: selectedStandards.length,
        standards: selectedStandards.map(s => s.label),
        subject: subjectSelected.label,
        grade: gradeSelected,
        searchValue,
        dropdownOption: selectedState ?? selectedNationalStandard,
      });
      onStandardsSelected(selectedStandards);
    }
  };

  const isAtMaxStandards = selectedStandards.length >= MAX_STANDARDS;

  const toggleStandard = (standard: Standards) => {
    setSelectedStandards(prev => {
      const isSelected = prev.some(s => s.id === standard.id);
      if (isSelected) {
        return prev.filter(s => s.id !== standard.id);
      }
      // Don't add if at max
      if (prev.length >= MAX_STANDARDS) {
        return prev;
      }
      return [...prev, standard];
    });
  };

  const removeStandard = (standardId: string) => {
    setSelectedStandards(prev => prev.filter(s => s.id !== standardId));
  };

  useEffect(() => {
    resetGeneratedStandards();
    handleGenerateStandards('Top 5 standards');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeSelected, selectedState, subjectSelected]);

  return (
    <ModalContentContainer>
      {paywallComponent}
      <div style={{ marginBottom: 15, padding: '20px 20px 0px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
          <h1
            style={{
              textAlign: 'left',
              fontSize: 24,
              fontWeight: 'bold',
              marginBottom: 0,
            }}
          >
            Align to skill or standard{' '}
          </h1>
          <Tooltip
            title={
              hasAccessToSchoolPlan
                ? "Available to you as you're on the school plan"
                : 'Available on the school plan'
            }
          >
            <IconWrapper
              height="24px"
              width="24px"
              style={{ marginLeft: 10, marginBottom: 5 }}
            >
              <EnterprisePlanIcon
                color={hasAccessToSchoolPlan ? theme.colors.black : theme.colors.yellow}
              />
            </IconWrapper>
          </Tooltip>
        </div>
        {isDesktop && (
          <>
            <p
              style={{
                textAlign: 'left',
                fontSize: 14,
                fontWeight: 'bold',
                marginBottom: 0,
              }}
            >
              Choose a skill or standard below
            </p>
            {!hasAccessToSchoolPlan && (
              <p
                style={{
                  textAlign: 'left',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 0,
                  color: '#177655',
                }}
              >
                Aligning lessons to skills and standards is available on the School Plan.{' '}
                <GetAccessCTASpan
                  onClick={() =>
                    promptPaywall({ description: 'standards', label: 'standards' })
                  }
                >
                  Get access here!
                </GetAccessCTASpan>
              </p>
            )}
          </>
        )}
        {!isDesktop && (
          <>
            {!hasAccessToSchoolPlan && (
              <p
                style={{
                  textAlign: 'left',
                  fontSize: 14,
                  fontWeight: 'bold',
                  marginBottom: 0,
                  color: '#177655',
                }}
              >
                Aligning lessons to skills and standards is available on the School Plan.{' '}
                <GetAccessCTASpan
                  onClick={() =>
                    promptPaywall({ description: 'standards', label: 'standards' })
                  }
                >
                  Get access here!
                </GetAccessCTASpan>
              </p>
            )}
          </>
        )}
        {canSwitchSubject && (
          <SubjectButtonsContainer>
            <SubjectButton
              styles={{
                color:
                  subjectSelected.id === 'ela'
                    ? theme.colors.primary
                    : theme.colors.black,
              }}
              type="linkStyle"
              $selected={subjectSelected.id === 'ela'}
              onClick={() => {
                setSubjectSelected({ id: 'ela', label: 'ELA' });
              }}
            >
              {' '}
              ELA
            </SubjectButton>
            <SubjectButton
              styles={{
                color:
                  subjectSelected.id === 'math'
                    ? theme.colors.primary
                    : theme.colors.black,
              }}
              type="linkStyle"
              $selected={subjectSelected.id === 'math'}
              onClick={() => {
                setSubjectSelected({ id: 'math', label: 'Math' });
              }}
            >
              {' '}
              Math
            </SubjectButton>
            <SubjectButton
              styles={{
                color:
                  subjectSelected.id === 'science'
                    ? theme.colors.primary
                    : theme.colors.black,
              }}
              type="linkStyle"
              $selected={subjectSelected.id === 'science'}
              onClick={() => {
                setSubjectSelected({ id: 'science', label: 'Science' });
              }}
            >
              {' '}
              Science
            </SubjectButton>
            <SubjectButton
              styles={{
                color:
                  subjectSelected.id === 'socialStudies'
                    ? theme.colors.primary
                    : theme.colors.black,
              }}
              type="linkStyle"
              $selected={subjectSelected.id === 'socialStudies'}
              onClick={() => {
                setSubjectSelected({ id: 'socialStudies', label: 'Social Studies' });
              }}
            >
              {' '}
              Social Studies
            </SubjectButton>
          </SubjectButtonsContainer>
        )}
      </div>
      <div style={{ padding: '0px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <p
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 'bold',
              color: theme.colors.black,
              marginBottom: 5,
            }}
          >
            Choose a level
          </p>
          <span
            style={{
              fontSize: 12,
              color: theme.colors.green,
              cursor: 'pointer',
            }}
            onClick={() =>
              showNewMessages(
                toStandardsIntercomMessage({
                  stateDropdownOption: selectedState ?? selectedNationalStandard,
                  subject: subjectSelected.label,
                  grade: gradeSelected,
                }),
              )
            }
          >{`Don't see your standards? Request here`}</span>
        </div>
        <InputsContainer>
          <FloatingSelect
            value={selectedState ?? selectedNationalStandard}
            onSelect={handleSetStateDropdownOption}
            options={dropdownOptions}
            placeholder="Choose state"
          />
          <FloatingSelect
            value={gradeSelected}
            onSelect={handleSetGrade}
            options={gradeOptions}
            placeholder="Choose grade"
          />

          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            isGenerateStandardsEnabled={mustGenerateStandards}
            isGenerating={isGeneratingStandards}
            handleGenerateStandards={handleGenerateStandards}
            placeholder={searchPlaceholderMapper(subjectSelected.id)}
          />
        </InputsContainer>
      </div>
      <div
        style={{
          overflow: 'auto',
          paddingBottom: 66,
          borderRadius: '0px 0px 10px 10px',
        }}
      >
        <StrandSelectionSection
          strands={strands}
          isLoading={isFetchingStandards}
          selectedStandards={selectedStandards}
          toggleStandard={toggleStandard}
          searchValue={searchValue}
          state={selectedState}
          nationalStandard={selectedNationalStandard}
          subjectSelected={subjectSelected}
          gradeSelected={gradeSelected}
          generatedStandards={generatedStandards}
          mustGenerateStandards={mustGenerateStandards}
          isGeneratingStandards={isGeneratingStandards}
          disabled={!hasAccessToSchoolPlan}
          isAtMaxStandards={isAtMaxStandards}
        />
      </div>
      <BottomSelectedContainer>
        {hasAccessToSchoolPlan && selectedStandards.length > 0 && (
          <>
            <SelectedStandardsContainer>
              {selectedStandards.map(standard => (
                <Label key={standard.id} style={{ marginBottom: 0, display: 'flex' }}>
                  <IconWrapper
                    height="8px"
                    width="8px"
                    style={{ marginRight: 10, cursor: 'pointer' }}
                    onClick={() => removeStandard(standard.id)}
                  >
                    <CrossSvg />
                  </IconWrapper>
                  {standard.label}
                </Label>
              ))}
              {isAtMaxStandards && (
                <span style={{ fontSize: 12, color: theme.colors.grey, marginLeft: 8 }}>
                  (Maximum {MAX_STANDARDS} standards)
                </span>
              )}
            </SelectedStandardsContainer>
            <Button
              type="primary"
              height="42px"
              styles={{ color: 'white', background: theme.colors.orange }}
              onClick={handleConfirm}
            >
              {isDesktop
                ? `Set ${selectedStandards.length} standard${
                    selectedStandards.length > 1 ? 's' : ''
                  }`
                : 'Set'}
            </Button>
          </>
        )}
        {hasAccessToSchoolPlan && selectedStandards.length === 0 && (
          <>
            <div></div>
            <Button
              type="primary"
              height="42px"
              styles={{
                color: 'white',
                background: theme.colors.grey,
              }}
              disabled={true}
            >
              {isDesktop ? 'Set skills / standards' : 'Set'}
            </Button>
          </>
        )}
        {!hasAccessToSchoolPlan && (
          <>
            <div></div>
            <Button
              type="primary"
              height="42px"
              onClick={() =>
                promptPaywall({ description: 'standards', label: 'standards' })
              }
            >
              {isDesktop ? 'Get access' : 'Get access'}
            </Button>
          </>
        )}
      </BottomSelectedContainer>
    </ModalContentContainer>
  );
};
const GetAccessCTASpan = styled.span`
  font-weight: 800;
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    text-decoration: none;
  }
`;

const SubjectButton = styled(Button)<{ $selected?: boolean }>`
  margin-right: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: ${({ $selected }) => ($selected ? 'default' : 'pointer')};
  text-decoration: ${({ $selected }) => ($selected ? 'underline' : 'none')};

  &:hover {
    text-decoration: underline !important;
  }
`;

const SubjectButtonsContainer = styled.div`
  display: flex;
  margin: 10px 0px;
`;

const ModalContentContainer = styled.div`
  height: 80vh;
  display: grid;
  grid-template-rows: min-content min-content auto;
  position: relative;
  @media (max-width: 700px) {
    height: 85vh;
  }
`;

const InputsContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 10px;
  grid-template-columns: 25vw 15vw auto;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const BottomSelectedContainer = styled.div`
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  min-height: 66px;
  background: white;

  border-radius: 0px 0px 10px 10px;
  box-shadow: 0px -2px 6px rgba(0, 0, 0, 0.13);
  display: grid;
  grid-template-columns: auto 200px;
  padding: 10px 20px;
  align-items: center;
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: auto 80px;
  }
`;

const SelectedStandardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 80px;
  overflow-y: auto;
`;

const StrandSelectionSection = ({
  selectedStandards,
  toggleStandard,
  strands,
  isLoading,
  searchValue,
  subjectSelected,
  gradeSelected,
  state,
  nationalStandard,
  generatedStandards,
  isGeneratingStandards,
  mustGenerateStandards,
  disabled,
  isAtMaxStandards,
}: {
  isGeneratingStandards?: boolean;
  generatedStandards?: PromptResourceMap;
  selectedStandards: Standards[];
  toggleStandard: (standard: Standards) => void;
  strands: AugmentedStandards[] | undefined;
  isLoading: boolean;
  searchValue: string | undefined;
  state?: string;
  nationalStandard?: string;
  subjectSelected: { id: string; label: string };
  gradeSelected: string | undefined;
  mustGenerateStandards: boolean;
  disabled: boolean;
  isAtMaxStandards: boolean;
}) => {
  const { showNewMessages } = useIntercom();
  const selectedSetOfStandards = useMemo(() => {
    return nationalStandard ? nationalStandard : state === 'TX' ? 'TEKS' : 'CCSS';
  }, [nationalStandard, state]);

  //Filter the strands based on the standard, subject and grade selected
  const memoizedFilteredStrands: AugmentedStandards[] = useMemo(() => {
    if (!subjectSelected || !gradeSelected || mustGenerateStandards) return [];

    return (
      strands?.filter(strand => {
        const isStandardMatch = strand.standardsSet === selectedSetOfStandards;
        const isSubjectMatch = strand.subject === subjectSelected.id;
        const isGradeMatch = strand.grades.includes(gradeSelected);
        return isStandardMatch && isSubjectMatch && isGradeMatch;
      }) || []
    );
  }, [
    strands,
    selectedSetOfStandards,
    subjectSelected,
    gradeSelected,
    mustGenerateStandards,
  ]);

  const searchedStrands = useMemo(
    () =>
      memoizedFilteredStrands?.filter(strand => {
        if (!searchValue) return true;
        const combinedText = `${strand.label} ${strand.description}`.toLowerCase();

        const searchTerms = searchValue
          .split(' ')
          .map(term => term.trim())
          .filter(term => term);

        const matchesAllTerms = (text: string) => {
          return searchTerms.every(term => {
            // The regex takes each substring of the search value and escapes special characters (like . * + ? etc.)
            // to ensure they are treated as literal characters. It then creates a case-insensitive pattern to match
            // the substring anywhere within the either the label or description of the standard.
            const regex = new RegExp(term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
            return regex.test(text);
          });
        };
        return matchesAllTerms(combinedText);
      }),
    [memoizedFilteredStrands, searchValue],
  );

  //reduce the generated standards to an array
  const generatedStandardsArray = Object.keys(generatedStandards || {})?.reduce(
    (acc, key) => {
      if (!generatedStandards) return acc;
      // Extract the index from the key
      const indexMatch = key.match(/standards-output\.(\d+)\./);
      if (!indexMatch) return acc;

      const index = parseInt(indexMatch[1], 10);
      const fieldName = key.split('.').pop();

      if (!acc[index]) {
        acc[index] = { id: generatedStandards[key].id }; // Initialize the object with the id
      }

      if (fieldName === 'standard-code') {
        acc[index]['standard-code'] = generatedStandards[key].value;
      } else if (fieldName === 'standard-description') {
        acc[index]['standard-description'] = generatedStandards[key].value;
      } else if (fieldName === 'standard-relevancy') {
        acc[index]['standard-relevancy'] = generatedStandards[key].value;
      }

      return acc;
    },
    [] as GeneratedStandard[],
  );

  const highlightText = (text: string, searchTerms: string[]) => {
    if (!searchTerms.length) return text;

    // Escape special characters for regex
    const regex = new RegExp(
      searchTerms.map(term => term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'),
      'gi',
    );

    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          const match = text.match(regex)?.[index];
          return (
            <React.Fragment key={index}>
              {part}
              {match && (
                <span
                  style={{
                    backgroundColor: '#e6d5c0',
                    fontWeight: 'bold',
                  }}
                >
                  {match}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const searchTerms =
    searchValue
      ?.split(' ')
      .map((term: string) => term.trim())
      .filter((term: string) => term) || [];

  const noStrandsFound = !!searchedStrands && searchedStrands.length === 0;
  const hasGeneratedStandards =
    !!generatedStandardsArray && generatedStandardsArray.length > 0;
  const isSearching = isLoading || isGeneratingStandards;
  const shouldShowNoStandardsMessage =
    strands && !isSearching && noStrandsFound && !hasGeneratedStandards;

  return (
    <StandardSelectionSectionContainer>
      {!!strands && (
        <>
          {/* ELA Specific Strands */}
          {!mustGenerateStandards &&
            !!searchedStrands &&
            searchedStrands.map(strand => (
              <Strand
                key={strand.label}
                $disabled={disabled}
                onClick={() => toggleStandard(strand)}
              >
                <div style={{ display: 'block' }}>
                  <Label>{strand.label}</Label>
                  <Description $disableMark={disabled}>
                    {highlightText(strand.description, searchTerms)}
                  </Description>
                </div>
                <div
                  style={{
                    display: 'grid',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ height: 24 }}>
                    <Checkbox
                      checked={selectedStandards.some(s => s.id === strand.id)}
                      disabled={
                        isAtMaxStandards &&
                        !selectedStandards.some(s => s.id === strand.id)
                      }
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleStandard(strand)}
                    />
                  </div>
                </div>
              </Strand>
            ))}
          {/* Non-ELA Strands - generated standards */}
          {generatedStandardsArray && (
            <>
              {generatedStandardsArray.map(generatedStandard => {
                const standardObj: Standards = {
                  id: generatedStandard.id + generatedStandard['standard-code'],
                  label: generatedStandard['standard-code'] || '',
                  description: `${generatedStandard['standard-description']} ${
                    generatedStandard['standard-relevancy']
                      ? generatedStandard['standard-relevancy']
                      : ''
                  }`,
                };
                return (
                  <Strand
                    key={generatedStandard.id}
                    onClick={() => toggleStandard(standardObj)}
                    $disabled={disabled}
                  >
                    <div style={{ display: 'block' }}>
                      <Label>{generatedStandard['standard-code']}</Label>
                      <Description $disableMark={disabled}>
                        {highlightText(
                          `${generatedStandard['standard-description']} ${
                            generatedStandard['standard-relevancy']
                              ? generatedStandard['standard-relevancy']
                              : ''
                          }`,
                          searchTerms,
                        )}
                      </Description>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ height: 24 }}>
                        <Checkbox
                          checked={selectedStandards.some(s => s.id === standardObj.id)}
                          disabled={
                            isAtMaxStandards &&
                            !selectedStandards.some(s => s.id === standardObj.id)
                          }
                          onClick={e => e.stopPropagation()}
                          onChange={() => toggleStandard(standardObj)}
                        />
                      </div>
                    </div>
                  </Strand>
                );
              })}
            </>
          )}
          {isSearching && (
            <ResponsiveGeneratorsGridContainer
              style={{
                width: '100%',
                maxHeight: 180,
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 100, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            </ResponsiveGeneratorsGridContainer>
          )}
          {/* No Strands or Standards Found Message */}
          {shouldShowNoStandardsMessage && (
            <Strand
              $disableHover
              $hideBorder
              key={searchValue}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: 40,
                paddingBottom: 40,
              }}
            >
              {!mustGenerateStandards ? (
                <>
                  <IconWrapper height={'100px'} width={'100px'}>
                    <FoldedHandsSVG />
                  </IconWrapper>
                  <Label
                    style={{
                      fontSize: 20,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      showNewMessages(
                        toStandardsIntercomMessage({
                          stateDropdownOption: state ?? nationalStandard,
                          subject: subjectSelected.label,
                          grade: gradeSelected,
                        }),
                      )
                    }
                  >
                    {`Don't see your standards? Request here`}
                  </Label>
                </>
              ) : (
                <Label>Search your standard to get started</Label>
              )}
            </Strand>
          )}
        </>
      )}
    </StandardSelectionSectionContainer>
  );
};

const Strand = styled.div<{
  $disableHover?: boolean;
  $hideBorder?: boolean;
  $disabled?: boolean;
}>`
  padding: 10px 0px 10px 20px;
  color: ${theme.colors.black};
  font-weight: bold;
  text-align: left;
  transition: background-color 0.2s;
  width: 100%;
  display: flex;
  flex-direction: column;
  ${({ $hideBorder }) =>
    $hideBorder ? 'border-bottom: none;' : 'border-bottom: 1px solid #d9d9d9;'}
  padding-top: 10px;

  display: grid;
  grid-template-columns: auto 80px;

  &:hover {
    ${({ $disableHover, $disabled }) =>
      !$disableHover &&
      !$disabled &&
      `
     cursor: pointer;
     background-color: #e6d5c0;
     border-bottom: 1px solid transparent;
     `};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const Label = styled.p`
  font-size: 14px;
  padding: 2px 10px;
  border-radius: 5px;
  background: #cfede2;
  color: #177655;
  font-weight: 800;
  width: fit-content;
  margin-bottom: 5px;
`;

const Description = styled.p<{ $disableMark?: boolean }>`
  font-size: 16px;
  font-weight: 400;
  padding-left: 10px;
  font-weight: bold;
  margin-bottom: 0px;

  ${({ $disableMark }) =>
    $disableMark &&
    css`
      user-select: none;
    `}
`;

const SearchInputComp = styled.input`
  border-radius: 5px;
  background: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  outline: none;
  border: none;
  line-height: 22px;
  font-size: 16px;
  height: 100%;
  width: 100%;
  padding-left: 40px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const SearchInput = ({
  value,
  onChange,
  handleGenerateStandards,
  isGenerateStandardsEnabled,
  placeholder,
  isGenerating,
}: {
  value?: string;
  onChange: (val?: string) => void;
  handleGenerateStandards: (searchedValue: string) => void;
  isGenerateStandardsEnabled: boolean;
  placeholder: string;
  isGenerating?: boolean;
}) => {
  const handleSearchValueChanged = (value?: string) => {
    if (!value || value.length === 0) {
      onChange(undefined);
      return;
    }
    onChange(value);
  };

  const handleSearchValueChangedDebounced = useMemo(
    () => debounce(handleGenerateStandards, 1000),

    [handleGenerateStandards],
  );

  useEffect(() => {
    if (value && isGenerateStandardsEnabled) handleSearchValueChangedDebounced(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isGenerating) return;
    if (e.key === 'Enter') {
      handleGenerateStandards(e.currentTarget.value);
    }
  };

  return (
    <SearchInputContainer>
      <IconWrapper
        style={{
          position: 'absolute',
          left: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
        height="20px"
        width="20px"
        color="#5B5B5A"
      >
        <SearchSvg />
      </IconWrapper>
      <SearchInputComp
        placeholder={placeholder}
        value={value || ''}
        onChange={e => {
          handleSearchValueChanged(e.target.value);
        }}
        onKeyDown={handleKeyDown} // Add this line
      />
      {value && value.length > 0 && (
        <IconWrapper
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          onClick={() => handleSearchValueChanged('')}
          height="15px"
          width="15px"
          color="#5B5B5A"
        >
          <CrossSvg />
        </IconWrapper>
      )}
    </SearchInputContainer>
  );
};

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 42px;

  @media (max-width: 800px) {
    height: 42px;
  }
`;

const StandardSelectionSectionContainer = styled.ul`
  width: 100%;
  list-style: none; /* Remove bullet points */
  padding: 0; /* Remove padding */
  margin: 0; /* Remove margin */
`;
